import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { DataGridItem } from "components/data-grid";
import withStyles from "components/with-styles";
import styles from "./styles/VirtualCamSection.css.js";

const VirtualCamSection = ({
  intl,
  values,
  setValue,
  errors,
  allStreamSources,
  setAllStreamSources,
  selectedStreamSources,
  setSelectedStreamSources,
  handleAddVptzSource,
  classes,
}) => {
  const [hasVirtualCam, setHasVirtualCam] = useState(false);
  const [hasSky, setHasSky] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(
    () => {
      setHasVirtualCam(!!values.file?.virtualcam);
      if (values.file?.virtualcam?.sky_dome) {
        setHasSky(values.file.virtualcam.sky_dome !== "no_sky.png");
      }
    },
    [values.file?.virtualcam],
  );

  const handleVirtualCamToggle = e => {
    const enabled = e.target.value === "yes";
    setHasVirtualCam(enabled);

    if (enabled) {
      if (!values.file?.virtualcam) {
        setValue("file.virtualcam", {
          config_file: "https://bert.tiivii.com/efs/cinfo/home/admin/components_cfg/virtualcam/cfg/",
          sky_dome: "no_sky.png",
          scheduler: [],
        });
        setHasSky(false);
      }

      if (handleAddVptzSource) {
        const vptzExists = allStreamSources.some(source => source.label === "vptz");

        if (!vptzExists) {
          const vptzSource = {
            label: "vptz",
            cam_id: "vptz",
            proto: "rtsp",
            url: "vcam://PAN/?bitrate=6000000&width=1920&height=1080&pan=0&tilt=0&fov=90&dewarp_id=",
            description: "Virtual PTZ Camera",
          };

          handleAddVptzSource(vptzSource);
        }
      }
    } else {
      const newFile = { ...values.file };
      delete newFile.virtualcam;
      setValue("file", newFile);

      if (setAllStreamSources) {
        const vptzIndex = allStreamSources.findIndex(source => source.label === "vptz");
        setAllStreamSources(allStreamSources.filter(source => source.label !== "vptz"));

        if (setSelectedStreamSources && vptzIndex !== -1) {
          const newSelectedStreamSources = selectedStreamSources
            .filter(i => i !== vptzIndex)
            .map(i => (i > vptzIndex ? i - 1 : i));
          setSelectedStreamSources(newSelectedStreamSources);
        }

        const newStreamSources = values.file?.stream_sources?.filter(source => source.label !== "vptz") || [];
        setValue("file.stream_sources", newStreamSources);

        const currentVideoArray = values.file?.mixer?.video || [];
        const newVideoArray = currentVideoArray.filter(label => label !== "vptz");
        setValue("file.mixer.video", newVideoArray);

        const currentAudioArray = values.file?.mixer?.audio || [];
        const newAudioArray = currentAudioArray.filter(label => label !== "vptz");
        setValue("file.mixer.audio", newAudioArray);

        const currentPreselected = values.file?.mixer?.preselected || [];
        const newPreselected = currentPreselected.filter(label => label !== "vptz");
        setValue("file.mixer.preselected", newPreselected);

        const currentRecordings = values.file?.video_drains?.recordings || [];
        const newRecordings = currentRecordings.filter(rec => rec.src !== "vptz");
        setValue("file.video_drains.recordings", newRecordings);
      }
    }
  };

  const handleConfigFileChange = e => {
    setValue("file.virtualcam.config_file", e.target.value);
  };

  const handleSkyToggle = e => {
    const enabled = e.target.value === "yes";
    setHasSky(enabled);

    if (enabled) {
      setValue("file.virtualcam.sky_dome", "");
    } else {
      setValue("file.virtualcam.sky_dome", "no_sky.png");
      setValue("file.virtualcam.scheduler", []);
      setUploadError("");
      setUploadSuccess(false);
    }
  };

  const handleSkyDomeChange = e => {
    setValue("file.virtualcam.sky_dome", e.target.value);
  };

  const validateTimeFormat = time => {
    const timeRegex = /^\d{6}Z$/;
    return timeRegex.test(time);
  };

  const validateSchedulerEntry = entry => {
    if (!entry || typeof entry !== "object") {
      return false;
    }
    if (!entry.name || typeof entry.name !== "string") {
      return false;
    }
    if (!entry.time || typeof entry.time !== "string") {
      return false;
    }
    return validateTimeFormat(entry.time);
  };

  const handleFileUpload = event => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    setUploadError("");
    setUploadSuccess(false);

    if (file.type !== "application/json") {
      setUploadError(
        intl.formatMessage(
          { id: "roomTemplate.virtualcam.invalidFileType" },
          { defaultMessage: "Invalid file type. Please upload a JSON file." },
        ),
      );
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const jsonData = JSON.parse(e.target.result);

        if (!Array.isArray(jsonData)) {
          setUploadError(
            intl.formatMessage(
              { id: "roomTemplate.virtualcam.invalidFormat" },
              { defaultMessage: "Invalid format. The JSON file must contain an array of scheduler entries." },
            ),
          );
          return;
        }

        const invalidEntries = [];
        jsonData.forEach((entry, index) => {
          if (!validateSchedulerEntry(entry)) {
            invalidEntries.push(index + 1);
          }
        });

        if (invalidEntries.length > 0) {
          setUploadError(
            intl.formatMessage(
              { id: "roomTemplate.virtualcam.invalidEntries" },
              {
                defaultMessage: `Invalid entries found at positions: ${invalidEntries.join(
                  ", ",
                )}. Each entry must have "name" (string) and "time" (string in HHMMSSZ format).`,
              },
            ),
          );
          return;
        }

        setValue("file.virtualcam.scheduler", jsonData);
        setUploadSuccess(true);
        setUploadError("");
      } catch (error) {
        setUploadError(
          intl.formatMessage(
            { id: "roomTemplate.virtualcam.parseError" },
            { defaultMessage: "Error parsing JSON file. Please ensure the file contains valid JSON." },
          ),
        );
      }
    };

    reader.onerror = () => {
      setUploadError(
        intl.formatMessage(
          { id: "roomTemplate.virtualcam.readError" },
          { defaultMessage: "Error reading file. Please try again." },
        ),
      );
    };

    reader.readAsText(file);
    event.target.value = "";
  };

  return (
    <>
      <DataGridItem big>
        <Typography variant="h6" gutterBottom className={classes.title}>
          {intl.formatMessage({ id: "roomTemplate.virtualcam.title" }, { defaultMessage: "Virtual cam" })}
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup row value={hasVirtualCam ? "yes" : "no"} onChange={handleVirtualCamToggle}>
            <FormControlLabel
              value="yes"
              control={<Radio color="primary" />}
              label={intl.formatMessage({ id: "common.yes" }, { defaultMessage: "Yes" })}
            />
            <FormControlLabel
              value="no"
              control={<Radio color="primary" />}
              label={intl.formatMessage({ id: "common.no" }, { defaultMessage: "No" })}
            />
          </RadioGroup>
        </FormControl>
        {hasVirtualCam && (
          <div className={classes.formSection}>
            <TextField
              label={intl.formatMessage(
                { id: "roomTemplate.virtualcam.configFile" },
                { defaultMessage: "Config File URL" },
              )}
              value={values.file?.virtualcam?.config_file || ""}
              onChange={handleConfigFileChange}
              fullWidth
              error={!!errors?.file?.virtualcam?.config_file}
              helperText={errors?.file?.virtualcam?.config_file?.message}
              className={classes.inputField}
              placeholder="https://bert.tiivii.com/efs/cinfo/home/admin/components_cfg/virtualcam/cfg/"
            />
            <Typography variant="subtitle1" className={classes.skyTitle}>
              {intl.formatMessage(
                { id: "roomTemplate.virtualcam.skyQuestion" },
                { defaultMessage: "Do you want sky?" },
              )}
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup row value={hasSky ? "yes" : "no"} onChange={handleSkyToggle}>
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label={intl.formatMessage({ id: "common.yes" }, { defaultMessage: "Yes" })}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label={intl.formatMessage({ id: "common.no" }, { defaultMessage: "No" })}
                />
              </RadioGroup>
            </FormControl>
            {hasSky && (
              <>
                <TextField
                  label={intl.formatMessage(
                    { id: "roomTemplate.virtualcam.skyDome" },
                    { defaultMessage: "Sky Dome URL" },
                  )}
                  value={values.file?.virtualcam?.sky_dome || ""}
                  onChange={handleSkyDomeChange}
                  fullWidth
                  className={classes.inputField}
                  placeholder="https://bert.tiivii.com/efs/cinfo/home/admin/components_cfg/virtualcam/cfg/redondela_santa_marina_sky.jpg"
                />
                <div className={classes.schedulerSection}>
                  <Typography variant="subtitle1" className={classes.schedulerTitle}>
                    {intl.formatMessage({ id: "roomTemplate.virtualcam.scheduler" }, { defaultMessage: "Scheduler" })}
                  </Typography>
                  <Typography variant="body2" className={classes.schedulerInstructions}>
                    {intl.formatMessage(
                      { id: "roomTemplate.virtualcam.uploadInstructions" },
                      {
                        defaultMessage:
                          "Upload a JSON file containing an array of scheduler entries. Each entry must have:",
                      },
                    )}
                  </Typography>
                  <Typography variant="body2" component="div" className={classes.schedulerFormat}>
                    <ul>
                      <li>
                        <strong>name</strong>:{" "}
                        {intl.formatMessage(
                          { id: "roomTemplate.virtualcam.nameDescription" },
                          {
                            defaultMessage:
                              'String with the sky dome image path (e.g., "morning_sky/stadium_080000.jpg")',
                          },
                        )}
                      </li>
                      <li>
                        <strong>time</strong>:{" "}
                        {intl.formatMessage(
                          { id: "roomTemplate.virtualcam.timeDescription" },
                          { defaultMessage: 'String in HHMMSSZ format (e.g., "160000Z")' },
                        )}
                      </li>
                    </ul>
                  </Typography>
                  <Typography variant="body2" className={classes.schedulerExample}>
                    {intl.formatMessage(
                      { id: "roomTemplate.virtualcam.exampleFormat" },
                      {
                        defaultMessage:
                          'Example: [{"name": "morning_sky/stadium_080000.jpg", "time": "080000Z"}, {"name": "evening_sky/stadium_180000.jpg", "time": "180000Z"}]',
                      },
                    )}
                  </Typography>
                  <input
                    accept="application/json"
                    style={{ display: "none" }}
                    id="scheduler-upload-button"
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="scheduler-upload-button">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      className={classes.uploadButton}
                    >
                      {intl.formatMessage(
                        { id: "roomTemplate.virtualcam.uploadScheduler" },
                        { defaultMessage: "Upload Scheduler JSON" },
                      )}
                    </Button>
                  </label>
                  {uploadError && (
                    <Typography variant="body2" color="error" className={classes.errorMessage}>
                      {uploadError}
                    </Typography>
                  )}
                  {uploadSuccess && (
                    <div className={classes.successMessage}>
                      <CheckCircleIcon className={classes.successIcon} />
                      <Typography variant="body2" className={classes.successText}>
                        {intl.formatMessage(
                          { id: "roomTemplate.virtualcam.uploadSuccess" },
                          {
                            count: values.file?.virtualcam?.scheduler?.length || 0,
                            defaultMessage: `Successfully loaded ${values.file?.virtualcam?.scheduler?.length ||
                              0} scheduler entries`,
                          },
                        )}
                      </Typography>
                    </div>
                  )}
                  {values.file?.virtualcam?.scheduler?.length > 0 && (
                    <Typography variant="body2" className={classes.entriesCount}>
                      {intl.formatMessage(
                        { id: "roomTemplate.virtualcam.currentEntries" },
                        {
                          count: values.file.virtualcam.scheduler.length,
                          defaultMessage: `Current entries: ${values.file.virtualcam.scheduler.length}`,
                        },
                      )}
                    </Typography>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </DataGridItem>
    </>
  );
};

VirtualCamSection.propTypes = {
  intl: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object,
  allStreamSources: PropTypes.arrayOf(PropTypes.object),
  setAllStreamSources: PropTypes.func,
  selectedStreamSources: PropTypes.array,
  setSelectedStreamSources: PropTypes.func,
  handleAddVptzSource: PropTypes.func,
  classes: PropTypes.object.isRequired,
};

export default injectIntl(withStyles(styles)(VirtualCamSection));
